// summariseSowAllocation — pure reconciliation of a SOW's milestone
// payment totals against the SOW's current value, shown on the SOW value
// tile when the milestone tab is scoped to a single SOW (the caption
// renderer lives in MilestoneSummaryStrip). Comparison happens at cent
// precision so float drift in summed payment amounts never reports a
// phantom over/under allocation. Governance ($0 / no payment) milestones
// contribute 0 but are counted — same locked rule as summariseMilestones.

import type { Milestone } from "../../../../types";

export type SowAllocationState = "reconciled" | "under" | "over";

export interface SowAllocationSummary {
  /** Sum of paymentAmount (?? 0) over every milestone in scope. */
  milestonesTotal: number;
  /** The SOW's current value the totals were compared against. */
  sowValue: number;
  /** Absolute difference, 0 when reconciled. `under` = unallocated
   *  remainder; `over` = excess above the SOW value. */
  difference: number;
  state: SowAllocationState;
  milestoneCount: number;
}

export function summariseSowAllocation(
  milestones: readonly Milestone[],
  sowValue: number,
): SowAllocationSummary {
  const totalCents = milestones.reduce(
    (sum, m) => sum + Math.round((m.paymentAmount ?? 0) * 100),
    0,
  );
  const sowCents = Math.round(sowValue * 100);
  const deltaCents = sowCents - totalCents;
  return {
    milestonesTotal: totalCents / 100,
    sowValue,
    difference: Math.abs(deltaCents) / 100,
    state: deltaCents === 0 ? "reconciled" : deltaCents > 0 ? "under" : "over",
    milestoneCount: milestones.length,
  };
}

/** Full-precision dollar figure for the reconciliation caption — this
 *  line exists to reconcile totals, so cents matter. */
export function formatAllocationAmount(value: number): string {
  return `$${value.toLocaleString("en-AU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}
